import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { http, formatApiError } from "../../lib/api";

export const safeDelete = async (url, done) => {
  try { await http.delete(url); toast.success("Supprimé"); done(); } catch (e) { toast.error(formatApiError(e)); }
};

export const inp = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200";
export const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50";
export const btnPrimary = `${btn} bg-[#0B2A7A] text-white hover:bg-[#0a2366]`;
export const btnGhost = `${btn} border border-slate-300 hover:bg-slate-50`;
export const iconBtn = "p-1.5 rounded hover:bg-slate-100 text-slate-600";
export const eur = (n) => Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
export const frDate = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("fr-FR") : "—");
export const today = () => new Date().toISOString().slice(0, 10);
export const agentLabel = (a) => `${a.first_name || ""} ${a.last_name || ""}`.trim();

export const F = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</span>{children}</label>
);

export const Modal = ({ title, onClose, children, wide, testid }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className={`${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`} data-testid={testid}>
      <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const STATUS = {
  active: ["En service", "bg-emerald-100 text-emerald-800"], returned: ["Restitué", "bg-slate-200 text-slate-700"],
  pending: ["En attente", "bg-amber-100 text-amber-800"], validated: ["Validé", "bg-sky-100 text-sky-800"], refused: ["Refusé", "bg-rose-100 text-rose-700"],
  ordered: ["Commandé", "bg-indigo-100 text-indigo-800"], delivered: ["Livré", "bg-emerald-100 text-emerald-800"], covered: ["Couvert", "bg-emerald-100 text-emerald-800"],
  draft: ["Brouillon", "bg-amber-100 text-amber-800"], sent: ["Envoyé", "bg-indigo-100 text-indigo-800"], received: ["Réceptionné", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Annulé", "bg-slate-200 text-slate-600"],
};
export const StatusBadge = ({ s }) => {
  const [l, c] = STATUS[s] || [s, "bg-slate-100 text-slate-600"];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${c}`} data-testid={`status-${s}`}>{l}</span>;
};

export const useList = (url, params) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    const n = ++seq.current;
    setLoading(true);
    return http.get(url, { params }).then((r) => { if (n === seq.current) setRows(r.data); })
      .catch((e) => { if (n === seq.current) toast.error(`Chargement impossible : ${formatApiError(e)}`); })
      .finally(() => { if (n === seq.current) setLoading(false); });
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return [rows, load, loading];
};

export const useEqSettings = () => {
  const [s, setS] = useState({ material_types: [], categories: [], reorder_block_months: 0, is_validator: false });
  const load = useCallback(() => http.get("/equipment/settings").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [s, load];
};

export const Empty = ({ children }) => <div className="px-4 py-8 text-center text-sm text-slate-400">{children}</div>;

export const Table = ({ head, children, testid }) => (
  <div className="border border-slate-200 rounded-md bg-white overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>{head.map((h) => <th key={h} className="text-left px-3 py-2 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  </div>
);
